import React, { useEffect, useRef, useState } from "react"
import face1 from "../assets/face1.jpg"
import face2 from "../assets/face2.jpg"
import home1 from "../assets/home1.jpg"
import home2 from "../assets/home2.jpg"
import home3 from "../assets/home3.jpg"
import image1 from "../assets/main1.jpg"
import image5 from "../assets/main5.jpeg"
import image6 from "../assets/main6.jpeg"
import award from "../assets/award-icon.png"
import cost from "../assets/cost.png"
import tools from "../assets/tools-icon.png"
import bedRoom from "../assets/bed_room.jpg"
import dinningRoom from "../assets/dining_room.jpg"
import livingRoom from "../assets/room_living_room.jpg"
import { Chart, ChartTypeRegistry, registerables } from "chart.js"
import "chartjs-adapter-date-fns"
import { Link } from "react-router-dom"

Chart.register(...registerables)

interface ChartData {
  type: keyof ChartTypeRegistry
  data: any
  options?: any
}

export const Home = () => {
  const chart1Ref = useRef<HTMLCanvasElement>(null)
  const chart2Ref = useRef<HTMLCanvasElement>(null)

  const [opacity, setOpacity] = useState(1)

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY
      const imageHeight = window.innerHeight // Assuming image is full screen height
      const fadeStartThreshold = imageHeight / 150 // Fade starts earlier
      const fadeEndThreshold = imageHeight / 2 // Fade completes sooner

      if (scrollPosition > fadeStartThreshold) {
        let newOpacity = 1
        if (scrollPosition > fadeEndThreshold) {
          newOpacity = 0 // Fully transparent after fadeEndThreshold
        } else {
          // Calculate opacity based on scroll position between fadeStartThreshold and fadeEndThreshold
          newOpacity = Math.max(
            0,
            1 -
              (scrollPosition - fadeStartThreshold) /
                (fadeEndThreshold - fadeStartThreshold)
          )
        }
        setOpacity(newOpacity)
      } else {
        setOpacity(1)
      }
    }

    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  useEffect(() => {
    let myChart1: Chart | null = null
    let myChart2: Chart | null = null

    const chart1Data: ChartData = {
      type: "doughnut",
      data: {
        labels: [
          "Recycled Materials",
          "Reduced Waste",
          "Energy Efficiency",
          "Water Conservation",
          "Sustainable Sourcing",
        ],
        datasets: [
          {
            label: "Environmental Impact Score (%)",
            data: [75, 85, 90, 60, 70], // Example data for each category
            backgroundColor: [
              "#4CAF50", // Recycled Materials
              "#8BC34A", // Reduced Waste
              "#CDDC39", // Energy Efficiency
              "#FFEB3B", // Water Conservation
              "#FF9800", // Sustainable Sourcing
            ],
            borderWidth: 0,
            hoverOffset: 4,
          },
        ],
      },

      options: {
        responsive: true,
        plugins: {
          legend: {
            display: true,
            position: "bottom",
          },
          title: {
            display: false,
          },
        },
      },
    }
    const chart2Data: ChartData = {
      type: "bar",
      data: {
        labels: ["Layouts", "Finishes", "Features", "Energy Options"],
        datasets: [
          {
            label: "Modular Homes",
            data: [15, 25, 12, 8],
            backgroundColor: "#673AB7",
          },
          {
            label: "Traditional Homes",
            data: [8, 15, 5, 3],
            backgroundColor: "#9575CD",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
          },
          title: {
            display: false,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: "Number of Options",
            },
          },
        },
      },
    }

    const initializeCharts = () => {
      if (chart1Ref.current) {
        myChart1 = new Chart(chart1Ref.current, chart1Data)
      }

      if (chart2Ref.current) {
        myChart2 = new Chart(chart2Ref.current, chart2Data)
      }
    }

    const destroyCharts = () => {
      if (myChart1) {
        myChart1.destroy()
        myChart1 = null
      }

      if (myChart2) {
        myChart2.destroy()
        myChart2 = null
      }
    }

    initializeCharts()

    return () => {
      destroyCharts()
    }
  }, [])

  return (
    <section className="w-full min-h-screen flex flex-col gap-24">
      <div className="w-full relative object-cover">
        <img
          src={image1}
          alt="1"
          className="w-full object-cover min-w-0 h-screen"
        />
        <div
          className="absolute bottom-0 left-0 w-full text-white p-4 transition-opacity duration-300 bg-black bg-opacity-40"
          style={{ opacity, willChange: "opacity" }}
        >
          <h2 className="md:text-9xl text-7xl anton-sc-regular ">
            LIVE SIMPLE, DREAM BIG.
          </h2>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-start py-12 px-12">
        <div className="xl:w-1/4 lg:w-1/3 md:w-1/2 mb-4 md:mb-0">
          <img
            src={image5}
            alt={"Custom layout options"}
            className="w-full rounded-2xl shadow-lg"
          />
        </div>
        <div className="xl:w-3/4 lg:w-2/3 md:w-1/2 px-4 md:px-8 gap-5 flex flex-col h-full">
          <h3 className="xl:text-5xl lg:text-4xl md:text-3xl text-3xl font-semibold text-gray-800 mb-3">
            Custom Layout Options
          </h3>
          <p className="xl:text-2xl lg:text-1xl md:text-base text-gray-600">
            Unleash your creativity and design a modular home that perfectly
            reflects your lifestyle.
          </p>
          <p className="xl:text-2xl lg:text-1xl md:text-base text-gray-600">
            Choose from a wide array of module combinations, from cozy
            single-story layouts to spacious two-story designs.
          </p>
          <div>
            <p className="xl:text-2xl lg:text-1xl md:text-base text-gray-600">
              Customize the placement of bedrooms, bathrooms, living areas, and
              kitchens to create a home that's uniquely yours.
            </p>
            <p className="xl:text-2xl lg:text-1xl md:text-base text-gray-600">
              Our expert designers will work with you to bring your vision to
              life, ensuring a functional and aesthetically pleasing space.
            </p>
          </div>
          <div>
            <p className="xl:text-2xl lg:text-1xl md:text-base text-gray-600">
              Imagine the possibilities: a sun-drenched living room with
              panoramic views, a gourmet kitchen designed
            </p>
            <p className="xl:text-2xl lg:text-1xl md:text-base text-gray-600">
              for culinary adventures, or a luxurious master suite with a
              spa-like bathroom.
            </p>
          </div>
          {/* Push the link to the bottom of the main container */}
          <div className="mt-auto">
            <Link
              to="/offer"
              className="inline-block bg-green-800 text-white py-3 px-8 rounded-full hover:bg-green-600 font-semibold
      transition
      duration-300
      ease-in-out
      hover:-translate-y-1
      hover:scale-110
      "
            >
              Discover Your Possibilities
            </Link>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-start py-12 px-12">
        <div className="xl:w-3/4 lg:w-2/3 md:w-1/2 px-4 md:px-8 gap-5 flex flex-col items-end">
          <h3 className="xl:text-5xl lg:text-4xl md:text-3xl text-3xl font-semibold text-gray-800 mb-3">
            Personalized Finishes
          </h3>
          <div>
            <p className="xl:text-2xl lg:text-1xl md:text-base text-gray-600">
              At Modular Home, our process is streamlined for efficiency and
              quality. It begins with a consultation to understand your vision
            </p>
          </div>
          <p className="xl:text-2xl lg:text-1xl md:text-base text-gray-600">
            Our designers then craft a custom plan, followed by precise module
            construction in our controlled factory.
          </p>

          <p className="xl:text-2xl lg:text-1xl md:text-base text-gray-600">
            While modules are built, we prep your site. Installation is swift,
            and finishing touches complete your dream home.
          </p>
          <p className="xl:text-2xl lg:text-1xl md:text-base text-gray-600">
            We prioritize sustainability and personalization every step of the
            way, delivering exceptional results with minimal disruption.
          </p>
          <div className="w-full flex justify-end">
            <Link
              to="/services"
              className="inline-block bg-green-800 text-white py-3 px-8 rounded-full hover:bg-green-600 font-semibold
              transition
              duration-300
              ease-in-out
              hover:-translate-y-1
              hover:scale-110
              "
            >
              Explore Our Process
            </Link>
          </div>
        </div>
        <div className="xl:w-1/4 lg:w-1/3 md:w-1/2 mb-4 md:mb-0">
          <img
            src={image6}
            alt={"Personalized finishes"}
            className="w-full rounded-2xl shadow-lg"
          />
        </div>
      </div>

      <div className="mx-auto py-16">
        <h2 className="text-3xl font-semibold text-gray-800 text-center mb-12">
          Explore Our Interior Designs
        </h2>

        <div className="w-full flex flex-col">
          <div className="w-full relative">
            <img
              src={bedRoom}
              alt="Interior Design 3"
              className="w-full object-cover"
            />
            <div className="absolute bottom-0 left-0 w-full bg-black bg-opacity-50 text-white p-4">
              <h3 className="text-lg md:text-xl font-semibold">Cozy Bedroom</h3>
              <p className="text-sm md:text-base">
                A relaxing and inviting bedroom with a focus on comfort and
                tranquility.
              </p>
            </div>
          </div>

          <div className="w-full relative">
            <img
              src={livingRoom}
              alt="Interior Design 1"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 w-full bg-black bg-opacity-50 text-white p-4">
              <h3 className="text-lg md:text-xl font-semibold">
                Modern Living Room
              </h3>
              <p className="text-sm md:text-base">
                A spacious and stylish living room with a focus on comfort and
                natural light.
              </p>
            </div>
          </div>

          <div className="w-full relative">
            <img
              src={dinningRoom}
              alt="Interior Design 2"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 w-full bg-black bg-opacity-50 text-white p-4">
              <h3 className="text-lg md:text-xl font-semibold">
                Elegant Kitchen
              </h3>
              <p className="text-sm md:text-base">
                A functional and beautiful kitchen with high-end appliances and
                ample storage space.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 md:px-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center mb-4 md:mb-12">
          Our Featured Modular Home Projects
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-3 gap-5 md:gap-10">
          {[home3, home1, home2].map((image, index) => (
            <Link
              to="./offer"
              key={index}
              style={{ backgroundImage: `url(${image})` }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden w-full aspect-[1/1] bg-cover bg-center transition-transform duration-500 hover:scale-105 relative group"
            >
              <div className="p-4 md:p-6 bg-black bg-opacity-40 h-full flex flex-col justify-end lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-500">
                <h3 className="text-xl md:text-2xl font-semibold text-white mb-2 md:mb-3">
                  {index === 0
                    ? "Modern Mountain Retreat"
                    : index === 1
                    ? "Urban Oasis"
                    : "Wooden Getaway"}
                </h3>
                <p className="text-gray-300 text-sm md:text-base">
                  {index === 0
                    ? "A stunning modular home nestled in the mountains, designed for comfort and breathtaking views."
                    : index === 1
                    ? "A stylish and sustainable modular home in the heart of the city, maximizing space and minimizing environmental impact."
                    : "A charming modular home in the woods, designed for relaxation and enjoying the nature lifestyle."}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-5xl md:text-5xl font-semibold text-gray-800 text-center">
          Discover the Advantages of Modular Homes
        </h2>
        <section className="flex lg:flex-row flex-col gap-10 py-8 md:py-12">
          <div className="w-full lg:w-1/2 mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 gap-4 md:gap-8">
              <div className="flex flex-col items-center text-center">
                <img
                  src={tools}
                  alt="Faster Construction"
                  className="w-20 h-20 object-cover mb-2 md:mb-4 mt-4 md:mt-8 animate-bounce"
                />
                <h3 className="text-2xl font-semibold text-gray-700 mb-1 md:mb-2">
                  Faster Construction
                </h3>
                <p className="text-gray-600  text-base">
                  Modular homes are built in a controlled factory environment,
                  significantly reducing construction time compared to
                  traditional methods.
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <img
                  src={cost}
                  alt="Cost-Effective"
                  className="w-20 h-20 object-cover mb-2 md:mb-4 mt-4 md:mt-8 animate-bounce"
                />
                <h3 className="text-2xl font-semibold text-gray-700 mb-1 md:mb-2">
                  Cost-Effective
                </h3>
                <p className="text-gray-600  text-base">
                  Controlled environments and efficient processes often lead to
                  lower construction costs, making modular homes a
                  budget-friendly option.
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <img
                  src={award}
                  alt="Superior Quality Control"
                  className="w-20 h-20 object-cover mb-2 md:mb-4 mt-4 md:mt-8 animate-bounce"
                />
                <h3 className="text-2xl font-semibold text-gray-700 mb-1 md:mb-2">
                  Superior Quality Control
                </h3>
                <p className="text-gray-600 text-base ">
                  Factory construction allows for rigorous quality control
                  checks at every stage, ensuring a higher level of precision
                  and durability.
                </p>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-1/2 mx-auto px-6 py-12 flex flex-wrap md:flex-row justify-center gap-8 md:gap-10">
            <div className="w-full">
              <div className="bg-white rounded-2xl p-4 md:p-6 shadow-md">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2 md:mb-3 text-center">
                  Environmental Impact Breakdown
                </h3>
                <canvas
                  ref={chart1Ref}
                  aria-label="Sustainability Chart"
                  role="img"
                  style={{ maxHeight: "250px" }}
                ></canvas>
                <p className="text-gray-600 mt-2 md:mt-3 text-center text-sm md:text-base">
                  We are committed to minimizing our environmental footprint
                  through sustainable practices.
                </p>
              </div>
            </div>

            <div className="w-full">
              <div className="bg-white rounded-2xl p-4 md:p-6 shadow-md">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2 md:mb-3 text-center">
                  Modular vs. Traditional Customization
                </h3>
                <canvas
                  ref={chart2Ref}
                  aria-label="Customization Chart"
                  role="img"
                  style={{ maxHeight: "250px" }}
                ></canvas>
                <p className="text-gray-600 mt-2 md:mt-3 text-center text-sm md:text-base">
                  Modular homes offer a wide range of customization options to
                  create a home that's uniquely yours.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
      <div className="mx-auto px-4 md:px-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center mb-4 md:mb-12">
          What Our Clients Are Saying
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
          <div className="bg-gray-50 rounded-2xl p-6 md:p-8 shadow-md">
            <p className="text-gray-700 italic mb-2 md:mb-4 text-sm md:text-base">
              "We were amazed by the speed and efficiency of the modular
              construction process. Our dream home was built in a fraction of
              the time compared to traditional methods, and the quality is
              outstanding!"
            </p>
            <div className="flex items-center">
              <img
                src={face1}
                alt="Client 1"
                className="w-10 md:w-12 h-10 md:h-12 rounded-full object-cover mr-3 md:mr-4 min-w-0"
              />
              <div>
                <h4 className="text-lg font-semibold text-gray-800">
                  John Smith
                </h4>
                <p className="text-gray-500 text-sm">Homeowner</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl p-6 md:p-8 shadow-md">
            <p className="text-gray-700 italic mb-2 md:mb-4 text-sm md:text-base">
              "We were looking for a sustainable and cost-effective building
              solution, and modular construction was the perfect fit. We are
              thrilled with our new home and the positive impact it has on the
              environment."
            </p>
            <div className="flex items-center">
              <img
                src={face2}
                alt="Client 2"
                className="w-10 md:w-12 h-10 md:h-12 rounded-full object-cover mr-3 md:mr-4 min-w-0"
              />
              <div>
                <h4 className="text-lg font-semibold text-gray-800">
                  Jane Doe
                </h4>
                <p className="text-gray-500 text-sm">Homeowner</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 md:px-6 text-center py-8 md:py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-green-800 mb-4 md:mb-8">
          Ready to Build Your Dream Modular Home?
        </h2>
        <p className="text-lg md:text-xl text-green-700 mb-6 md:mb-12">
          Contact us today for a free consultation and let us help you design
          and build the perfect modular home for your needs and lifestyle.
        </p>
        <Link
          to="/contact"
          className="inline-block bg-green-800 text-white py-3 px-8 rounded-full hover:bg-green-600 font-semibold
              transition
              duration-300
              ease-in-out
              hover:-translate-y-1
              hover:scale-110
              "
        >
          Get a Free Consultation
        </Link>
      </div>
    </section>
  )
}
